import * as Sentry from '@sentry/react'

/**
 * Sentry configuration for robcratch application
 * Handles error tracking, performance monitoring, and session replay
 *
 * Features implemented:
 * - Error boundary integration with custom fallback UI
 * - Session replay with privacy settings (masks sensitive data in production)
 * - Performance monitoring with configurable sample rates
 * - Breadcrumb collection for better error context
 * - Environment-specific configuration (development, staging, production)
 * - Source maps support for better stack traces
 * - Custom error filtering to reduce noise
 *
 * Environment variables required:
 * - VITE_SENTRY_DSN: Sentry project DSN
 * - VITE_APP_VERSION: Application version for release tracking
 * - VITE_BUILD_NUMBER: Build number for release identification
 *
 * Usage:
 * 1. Set up environment variables in .env files
 * 2. Initialize Sentry early in main.tsx (already done)
 * 3. Use SentryErrorBoundary to wrap your app (already done)
 * 4. Use utility functions (captureException, captureMessage, etc.) as needed
 *
 * Testing:
 * - In development, use sentryTest.runAllTests() in browser console
 * - Check Sentry dashboard for captured events
 */

const isDevelopment = import.meta.env.MODE === 'development'
const isProduction = import.meta.env.MODE === 'production'
const isStaging = import.meta.env.MODE === 'staging'

/**
 * Get the appropriate environment name for Sentry
 */
function getEnvironment(): string {
  if (isProduction) return 'production'
  if (isStaging) return 'staging'
  return 'development'
}

/**
 * Get the DSN from environment variables
 */
function getDsn(): string | undefined {
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (isDevelopment && !dsn) {
    return undefined
  }

  return dsn
}

/**
 * Custom error filter to exclude common development errors and noise
 */
function beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  // Filter out development-only errors
  if (isDevelopment) {
    // Skip React DevTools extension errors
    if (event.exception?.values?.[0]?.value?.includes('__REACT_DEVTOOLS_')) {
      return null
    }

    // Skip extension-related errors
    if (
      event.exception?.values?.[0]?.value?.includes(
        'Extension context invalidated',
      )
    ) {
      return null
    }

    // Skip ResizeObserver loop limit exceeded (common browser noise)
    if (
      event.exception?.values?.[0]?.value?.includes(
        'ResizeObserver loop limit exceeded',
      )
    ) {
      return null
    }
  }

  // Filter out network errors that are not actionable
  if (event.exception?.values?.[0]?.type === 'NetworkError') {
    return null
  }

  // Filter out script loading errors from browser extensions
  if (event.exception?.values?.[0]?.value?.includes('Loading CSS chunk')) {
    return null
  }

  return event
}

/**
 * Initialize Sentry with appropriate configuration for the current environment
 */
export function initializeSentry(): void {
  const dsn = getDsn()

  if (!dsn) {
    console.log('Sentry initialization skipped - no DSN provided')
    return
  }

  // Get replay configuration from environment variables
  const replaySessionSampleRate = parseFloat(
    import.meta.env.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE ?? '0',
  )
  const replayOnErrorSampleRate = parseFloat(
    import.meta.env.VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE ?? '1.0',
  )

  // Allow complete disabling of replay in development
  const disableReplay = import.meta.env.VITE_SENTRY_DISABLE_REPLAY === 'true'

  try {
    Sentry.init({
      dsn,
      environment: getEnvironment(),

      // Performance monitoring
      tracesSampleRate: isProduction ? 0.1 : 1.0, // Lower sample rate in production

      // Session replay configuration (cost optimization)
      // Use environment variables to control replay sampling
      replaysSessionSampleRate: disableReplay ? 0 : replaySessionSampleRate,
      replaysOnErrorSampleRate: disableReplay ? 0 : replayOnErrorSampleRate,

      // Error filtering
      beforeSend,

      // Integration configuration
      integrations: [
        // Browser tracing for performance monitoring
        Sentry.browserTracingIntegration({
          // Capture interactions like clicks, form submissions
          enableInp: true,

          // Track navigation timing
          enableLongTask: true,
        }),

        // Session replay with privacy settings
        Sentry.replayIntegration({
          // Don't mask text content - we want to see the actual content
          maskAllText: false,

          // Block media elements from recording to save bandwidth
          blockAllMedia: true,

          // Only mask password inputs for security
          maskAllInputs: false,

          // Mask specific input types
          mask: ['input[type="password"]', 'input[type="email"]'],

          // Additional privacy settings for production
          ...(isProduction && {
            // Block specific selectors from recording if needed
            block: ['.sensitive-data', '[data-sensitive]'],

            // Mask additional elements with personal data
            mask: [
              'input[type="password"]',
              'input[type="email"]',
              '.user-data',
              '[data-personal]',
            ],

            // Ignore specific DOM mutations
            ignoreClass: ['sentry-ignore'],
          }),
        }),

        // Context lines for better debugging
        Sentry.contextLinesIntegration(),

        // Breadcrumbs for better error context
        Sentry.breadcrumbsIntegration({
          console: !isProduction, // Only log console breadcrumbs in non-production
          dom: {
            serializeAttribute:
              isProduction ? [] : ['id', 'class', 'aria-label'],
          },
          fetch: true,
          history: true,
          sentry: true,
          xhr: true,
        }),
      ],

      // Release information
      release: new Date().toISOString() || 'unknown',

      // Debug mode for development
      debug: isDevelopment,

      // Additional options for production
      ...(isProduction && {
        // Send default PII (Personally Identifiable Information)
        sendDefaultPii: false,

        // Attach stack trace to messages
        attachStacktrace: true,

        // Maximum number of breadcrumbs
        maxBreadcrumbs: 50,
      }),
    })

    // Set additional context
    // Build number is automatically generated during build process
    const buildNumber =
      import.meta.env.VITE_BUILD_NUMBER || `deploy-${Date.now()}`

    Sentry.setContext('app', {
      name: 'robcratch',
      version: buildNumber,
      environment: getEnvironment(),
      build: buildNumber,
    })

    // Set user context if available
    const userAgent = navigator.userAgent
    const language = navigator.language

    Sentry.setContext('device', {
      userAgent,
      language,
      screenResolution: `${screen.width}x${screen.height}`,
      colorDepth: screen.colorDepth,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
  } catch (error) {
    console.error('Failed to initialize Sentry:', error)
  }
}

/**
 * Utility function to capture exceptions manually
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>,
): void {
  if (context) {
    Sentry.withScope((scope) => {
      scope.setContext('additional', context)
      Sentry.captureException(error)
    })
  } else {
    Sentry.captureException(error)
  }
}

/**
 * Utility function to capture messages manually
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
): void {
  Sentry.captureMessage(message, level)
}

/**
 * Utility function to add breadcrumb manually
 */
export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
  Sentry.addBreadcrumb(breadcrumb)
}

/**
 * Utility function to set user context
 */
export function setUser(user: Sentry.User): void {
  Sentry.setUser(user)
}

/**
 * Utility function to set tag
 */
export function setTag(key: string, value: string): void {
  Sentry.setTag(key, value)
}

/**
 * Error boundary component using Sentry
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary

/**
 * Performance measurement utilities
 */
export const withSentryProfiling = Sentry.withProfiler

/**
 * Export Sentry for advanced usage
 */
export { Sentry }
